import React from 'react';
import Input from 'rsuite/esm/Input';
import {
    AppUpdaterAdminBranch,
    AppUpdaterAdminStatus,
    AppUpdaterOidcAdminData,
    AppUpdaterOidcCaller,
    resolveApplicationDownloadUrl,
} from '../../../../shared/application_download';
import { langData } from '../../../../src/lang';
import { SystemShared } from '../../../../shared/system';
import { LangBlock, ShowNotify } from '../../global';
import { WebStorage } from '../../../storage';
import { AdminDataTable } from './admin.table';
import { AdminExternalLink } from './admin.user-actions';
import {
    AdminBadge,
    AdminButton,
    AdminCard,
    AdminEmpty,
    AdminFormField,
    AdminFormGrid,
    AdminGrid,
    AdminPageWrap,
    AdminSection,
    AdminStatCard,
} from './admin.ui';

const formatTimestamp = (ms: number | null, neverLabel: string) => {
    if (!ms) {
        return neverLabel;
    }

    return SystemShared.timeStampString(Math.floor(ms / 1000), true);
};

const formatInterval = (ms: number, translate: (id: langData, ...args: (number | string | boolean)[]) => string) => {
    const minutes = Math.round(ms / 60000);

    if (minutes < 1) {
        return translate('AdminIntervalSec', Math.round(ms / 1000));
    }

    return translate('AdminIntervalMin', minutes);
};

const BRANCH_LABELS: Record<string, langData> = {
    main: 'AdminBranchMain',
    beta: 'AdminBranchBeta',
    develop: 'AdminBranchDevelop',
};

const branchLabel = (id: string, translate: (id: langData) => string) =>
    BRANCH_LABELS[id] ? translate(BRANCH_LABELS[id]) : id;

const formatFileSize = (size?: number) => {
    if (!size) {
        return '';
    }

    if (size < 1024 * 1024) {
        return `${(size / 1024).toFixed(1)} KB`;
    }

    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const cachedCount = (branch: AppUpdaterAdminBranch) => {
    const ready = branch.assets.filter((asset) => asset.cached).length;

    return `${ready}/${branch.assets.length}`;
};

const copyText = async (
    text: string,
    label: string,
    strings: { copied: string; failed: string },
) => {
    try {
        await navigator.clipboard.writeText(text);
        ShowNotify(strings.copied, label, 'success');
    } catch {
        ShowNotify(strings.failed, label, 'error');
    }
};

const asOidcData = (res: { status: true; [key: string]: any }): AppUpdaterOidcAdminData => ({
    callers: Array.isArray(res.callers) ? res.callers : [],
    endpoint: String(res.endpoint || ''),
    audience: String(res.audience || ''),
    example: String(res.example || ''),
});

export const AdminApplication = () => {
    const [, , LangString] = LangBlock();
    const copyLabels = {
        copied: LangString('AdminCopied'),
        failed: LangString('AdminCopyFailed'),
    };
    const [status, setStatus] = React.useState<AppUpdaterAdminStatus | null>(null);
    const [loaded, setLoaded] = React.useState(false);
    const [error, setError] = React.useState('');
    const [resetting, setResetting] = React.useState(false);
    const [oidc, setOidc] = React.useState<AppUpdaterOidcAdminData | null>(null);
    const [oidcLoaded, setOidcLoaded] = React.useState(false);
    const [oidcSaving, setOidcSaving] = React.useState(false);
    const [repoDraft, setRepoDraft] = React.useState('');
    const [workflowsDraft, setWorkflowsDraft] = React.useState('');

    const applyStatus = (next: AppUpdaterAdminStatus) => {
        setStatus(next);
        setError('');
        setLoaded(true);
    };

    const loadStatus = React.useCallback(() => {
        return WebStorage.ApiRequest('/api/admin/application/updater').then((res) => {
            if (res.status) {
                applyStatus(res as AppUpdaterAdminStatus);

                return;
            }

            setStatus(null);
            setError(res.error_message || LangString('AdminUpdaterLoadFailed'));
            setLoaded(true);
        });
    }, []);

    React.useEffect(() => {
        loadStatus();
    }, [loadStatus]);

    const loadOidc = React.useCallback(() => {
        return WebStorage.ApiRequest('/api/admin/application/updater/oidc').then((res) => {
            if (res.status) {
                setOidc(asOidcData(res));
            } else {
                setOidc(null);
            }

            setOidcLoaded(true);
        });
    }, []);

    React.useEffect(() => {
        loadOidc();
    }, [loadOidc]);

    React.useEffect(() => {
        if (!status?.scanRunning) {
            return;
        }

        const timer = window.setInterval(() => {
            loadStatus();
        }, 3000);

        return () => {
            window.clearInterval(timer);
        };
    }, [status?.scanRunning, loadStatus]);

    const handleReset = () => {
        if (resetting) {
            return;
        }

        setResetting(true);

        WebStorage.ApiRequestPost('/api/admin/application/updater/reset').then((res) => {
            setResetting(false);

            if (!res.status) {
                ShowNotify(
                    LangString('AdminUpdaterCache'),
                    res.error_message || LangString('AdminResetFailed'),
                    'error',
                );

                return;
            }

            applyStatus(res as AppUpdaterAdminStatus);
            ShowNotify(
                LangString('AdminUpdaterCache'),
                LangString('AdminResetSuccess'),
                'success',
            );
        });
    };

    const saveCallers = (callers: AppUpdaterOidcCaller[]) => {
        if (oidcSaving) {
            return;
        }

        setOidcSaving(true);

        WebStorage.ApiRequestPost('/api/admin/application/updater/oidc', {
            callers: JSON.stringify(callers),
        }).then((res) => {
            setOidcSaving(false);

            if (!res.status) {
                ShowNotify(
                    LangString('AdminGithubActions'),
                    res.error_message || LangString('AdminOidcSaveFailed'),
                    'error',
                );

                return;
            }

            setOidc(asOidcData(res));
            ShowNotify(LangString('AdminGithubActions'), LangString('AdminOidcSaved'), 'success');
        });
    };

    const handleAddCaller = () => {
        const repo = repoDraft.trim();
        const workflows = workflowsDraft
            .split(/[\n,;]+/)
            .map((item) => item.trim())
            .filter(Boolean);

        if (!repo || workflows.length === 0) {
            ShowNotify(
                LangString('AdminGithubActions'),
                LangString('AdminOidcNeedRepo'),
                'error',
            );

            return;
        }

        const next = [...(oidc?.callers ?? [])];
        const existing = next.findIndex((item) => item.repo.toLowerCase() === repo.toLowerCase());

        if (existing >= 0) {
            const merged = Array.from(new Set([...next[existing].workflows, ...workflows]));

            next[existing] = { repo: next[existing].repo, workflows: merged };
        } else {
            next.push({ repo, workflows });
        }

        saveCallers(next);
        setRepoDraft('');
        setWorkflowsDraft('');
    };

    const handleRemoveCaller = (repo: string) => {
        saveCallers((oidc?.callers ?? []).filter((item) => item.repo !== repo));
    };

    const branches = status?.branches ?? [];

    return (
        <AdminPageWrap>
            <AdminSection
                title={LangString('AdminAppTitle')}
                subtitle={LangString('AdminAppSubtitle')}
                actions={
                    <>
                        <AdminButton
                            variant="ghost"
                            size="sm"
                            onClick={() => loadStatus()}
                            disabled={!loaded || resetting}
                        >
                            {LangString('AdminRefresh')}
                        </AdminButton>
                        <AdminButton
                            variant="primary"
                            size="sm"
                            onClick={handleReset}
                            disabled={!loaded || resetting}
                        >
                            {resetting ? LangString('AdminResetting') : LangString('AdminResetCache')}
                        </AdminButton>
                    </>
                }
            >
                {!loaded ? (
                    <AdminEmpty message={LangString('AdminLoadingUpdater')} />
                ) : error ? (
                    <AdminEmpty message={error} />
                ) : !status ? (
                    <AdminEmpty message={LangString('AdminNoUpdater')} />
                ) : (
                    <>
                        <AdminGrid columns={3}>
                            <AdminStatCard
                                label={LangString('AdminScanInterval')}
                                value={formatInterval(status.scanIntervalMs, LangString)}
                            />
                            <AdminStatCard
                                label={LangString('AdminLastScan')}
                                value={formatTimestamp(status.lastScanAt, LangString('AdminNever'))}
                            />
                            <AdminStatCard
                                label={LangString('AdminScanner')}
                                value={
                                    <AdminBadge tone={status.scanRunning ? 'warn' : 'ok'}>
                                        {status.scanRunning
                                            ? LangString('AdminRunning')
                                            : LangString('AdminIdle')}
                                    </AdminBadge>
                                }
                            />
                        </AdminGrid>

                        <div className="admin-application-table">
                            <AdminDataTable
                                data={branches}
                                rowKey={(row) => row.id}
                                emptyMessage={LangString('AdminNoBranches')}
                                columns={[
                                    {
                                        key: 'branch',
                                        label: LangString('AdminBranch'),
                                        primary: true,
                                        render: (row) => (
                                            <div className="admin-application-branch">
                                                <strong>{branchLabel(row.id, LangString)}</strong>
                                                <span className="admin-application-branch__id">
                                                    {row.id}
                                                </span>
                                            </div>
                                        ),
                                    },
                                    {
                                        key: 'access',
                                        label: LangString('AdminAccess'),
                                        render: (row) => (
                                            <AdminBadge
                                                tone={row.protected ? 'warn' : 'ok'}
                                            >
                                                {row.protected
                                                    ? LangString('AdminProtected')
                                                    : LangString('AdminPublic')}
                                            </AdminBadge>
                                        ),
                                    },
                                    {
                                        key: 'github',
                                        label: LangString('AdminGithubVersion'),
                                        render: (row) =>
                                            row.githubError && !row.githubVersion ? (
                                                <AdminBadge tone="danger">
                                                    {row.githubError}
                                                </AdminBadge>
                                            ) : (
                                                <code>{row.githubVersion || '—'}</code>
                                            ),
                                    },
                                    {
                                        key: 'cached',
                                        label: LangString('AdminCachedVersion'),
                                        render: (row) => (
                                            <code>{row.cachedVersion || '—'}</code>
                                        ),
                                    },
                                    {
                                        key: 'files',
                                        label: LangString('AdminFiles'),
                                        render: (row) => (
                                            <AdminBadge
                                                tone={
                                                    row.complete
                                                        ? 'ok'
                                                        : row.assets.length
                                                          ? 'warn'
                                                          : 'neutral'
                                                }
                                            >
                                                {row.assets.length
                                                    ? cachedCount(row)
                                                    : LangString('AdminNoAssets')}
                                            </AdminBadge>
                                        ),
                                    },
                                    {
                                        key: 'synced',
                                        label: LangString('AdminSynced'),
                                        hideMobile: true,
                                        render: (row) =>
                                            formatTimestamp(row.syncedAt, LangString('AdminNever')),
                                    },
                                    {
                                        key: 'repo',
                                        label: LangString('AdminRepo'),
                                        hideMobile: true,
                                        render: (row) => (
                                            <AdminExternalLink href={row.url}>
                                                {row.repo || row.url}
                                            </AdminExternalLink>
                                        ),
                                    },
                                ]}
                            />
                        </div>

                        <div className="admin-application-assets">
                            {branches.map((branch) => (
                                <div key={branch.id} className="admin-application-assets__block">
                                    <div className="admin-application-assets__head">
                                        {LangString('AdminBranchAssets', branchLabel(branch.id, LangString))}
                                        {branch.githubTag ? ` (${branch.githubTag})` : ''}
                                    </div>
                                    {branch.githubError ? (
                                        <p className="admin-application-assets__error">
                                            {branch.githubError}
                                        </p>
                                    ) : null}
                                    {branch.assets.length === 0 ? (
                                        <AdminEmpty message={LangString('AdminNoReleaseAssets')} />
                                    ) : (
                                        <ul className="admin-application-assets__list">
                                            {branch.assets.map((asset) => (
                                                <li key={asset.name}>
                                                    <AdminBadge
                                                        tone={asset.cached ? 'ok' : 'neutral'}
                                                    >
                                                        {asset.cached
                                                            ? LangString('AdminCached')
                                                            : LangString('AdminMissing')}
                                                    </AdminBadge>
                                                    <a
                                                        className="admin-link admin-application-assets__file"
                                                        href={resolveApplicationDownloadUrl(
                                                            asset.downloadUrl,
                                                        )}
                                                        download={asset.name}
                                                    >
                                                        {asset.name}
                                                    </a>
                                                    <span className="admin-application-assets__size">
                                                        {formatFileSize(asset.size)}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </AdminSection>

            <AdminSection
                title={LangString('AdminGithubActions')}
                subtitle={LangString('AdminGithubActionsSubtitle')}
            >
                {!oidcLoaded ? (
                    <AdminEmpty message={LangString('AdminLoadingOidc')} />
                ) : !oidc ? (
                    <AdminEmpty message={LangString('AdminOidcLoadFailed')} />
                ) : (
                    <>
                        <AdminCard title={LangString('AdminAllowedWorkflows')}>
                            <AdminFormGrid>
                                <AdminFormField label={LangString('AdminRepository')}>
                                    <Input
                                        value={repoDraft}
                                        placeholder="RocketMan-StreamKit/application"
                                        onChange={(value) => setRepoDraft(String(value))}
                                    />
                                </AdminFormField>
                                <AdminFormField label={LangString('AdminWorkflowFiles')} wide>
                                    <Input
                                        as="textarea"
                                        rows={3}
                                        value={workflowsDraft}
                                        placeholder={'release.yml\npromote.yml'}
                                        onChange={(value) => setWorkflowsDraft(String(value))}
                                    />
                                </AdminFormField>
                            </AdminFormGrid>
                            <div className="admin-application-oidc__add">
                                <AdminButton
                                    variant="primary"
                                    size="sm"
                                    disabled={oidcSaving}
                                    onClick={handleAddCaller}
                                >
                                    {oidcSaving
                                        ? LangString('AdminSaving')
                                        : LangString('AdminAddWorkflowAccess')}
                                </AdminButton>
                            </div>
                            <AdminDataTable
                                data={oidc.callers}
                                rowKey={(row) => row.repo}
                                emptyMessage={LangString('AdminNoWorkflows')}
                                columns={[
                                    {
                                        key: 'repo',
                                        label: LangString('AdminRepository'),
                                        primary: true,
                                        render: (row) => (
                                            <AdminExternalLink
                                                href={`https://github.com/${row.repo}`}
                                            >
                                                {row.repo}
                                            </AdminExternalLink>
                                        ),
                                    },
                                    {
                                        key: 'workflows',
                                        label: LangString('AdminWorkflowFiles'),
                                        render: (row) => (
                                            <div className="admin-application-oidc__files">
                                                {row.workflows.map((file) => (
                                                    <code key={file}>{file}</code>
                                                ))}
                                            </div>
                                        ),
                                    },
                                    {
                                        key: 'actions',
                                        label: LangString('AdminActions'),
                                        render: (row) => (
                                            <AdminButton
                                                variant="danger"
                                                size="sm"
                                                disabled={oidcSaving}
                                                onClick={() => handleRemoveCaller(row.repo)}
                                            >
                                                {LangString('AdminRemove')}
                                            </AdminButton>
                                        ),
                                    },
                                ]}
                            />
                        </AdminCard>

                        <AdminCard title={LangString('AdminCallFromGha')}>
                            <p className="admin-application-oidc__hint">
                                {LangString('AdminOidcHint1')}{' '}
                                <code>permissions: id-token: write</code>. {LangString('AdminOidcHint2')}{' '}
                                <code>core.getIDToken()</code> {LangString('AdminOidcHint3')}
                            </p>
                            <div className="admin-llm-url">
                                <span className="admin-llm-url__label">{LangString('AdminEndpoint')}</span>
                                <code className="admin-llm-url__value">{oidc.endpoint}</code>
                                <div className="admin-llm-url__actions">
                                    <AdminButton
                                        variant="primary"
                                        size="sm"
                                        onClick={() =>
                                            copyText(
                                                oidc.endpoint,
                                                LangString('AdminEndpoint'),
                                                copyLabels,
                                            )
                                        }
                                    >
                                        {LangString('AdminCopy')}
                                    </AdminButton>
                                </div>
                            </div>
                            <div className="admin-llm-url">
                                <span className="admin-llm-url__label">{LangString('AdminAudience')}</span>
                                <code className="admin-llm-url__value">{oidc.audience}</code>
                                <div className="admin-llm-url__actions">
                                    <AdminButton
                                        variant="primary"
                                        size="sm"
                                        onClick={() =>
                                            copyText(
                                                oidc.audience,
                                                LangString('AdminAudience'),
                                                copyLabels,
                                            )
                                        }
                                    >
                                        {LangString('AdminCopy')}
                                    </AdminButton>
                                </div>
                            </div>
                            <div className="admin-application-oidc__example-head">
                                <span>{LangString('AdminExampleWorkflow')}</span>
                                <AdminButton
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                        copyText(
                                            oidc.example,
                                            LangString('AdminWorkflowExample'),
                                            copyLabels,
                                        )
                                    }
                                >
                                    {LangString('AdminCopyYaml')}
                                </AdminButton>
                            </div>
                            <pre className="admin-application-code">{oidc.example}</pre>
                        </AdminCard>
                    </>
                )}
            </AdminSection>
        </AdminPageWrap>
    );
};
